import { useState } from 'react';
import { FrontendFilterValue, SelectedFilters } from '../types/filters.type';
import removeFilterValue, { isRangeFilter } from '../utils/filters';
import customIcons from '../utils/icons';
import AdvancedModal from './advancedModal';
import FilterSummary from './filterSummary';
import InformationText from './InformationText';
import Modal from './modal';
import useFilterStore from './streetview/filterStore';
import { FilterModalType } from '../types/enums';
import useAllFilterStore from '../shared/hooks/allFilterStore';

interface FilterModalProps {
  filters?: SelectedFilters
  type: FilterModalType
  isOpen: boolean
  onClose: () => void
}

export default function FilterModal({
  filters,
  type,
  isOpen,
  onClose,
}: FilterModalProps) {
  const selectedFilters = useFilterStore((state) => state.selectedFilters);
  const updateFilterStore = useFilterStore((state) => state.updateFilterStore);
  const [filterStoreCopy, setFilterStoreCopy] = useState<SelectedFilters>({});
  const { filterTypeLookup } = useAllFilterStore((state) => state);

  const [previousState, setPreviousState] = useState<{
    type: FilterModalType,
    isOpen: boolean,
    selectedFilters: SelectedFilters
  }>({
    type,
    isOpen: false,
    selectedFilters: {}
  });

  const removeFilter = (category: string, key: string, label: string, value: FrontendFilterValue) => {
    const isRange = isRangeFilter(key, filterTypeLookup);
    const updatedFilters = removeFilterValue(selectedFilters, category, key, label, value, isRange);
    updateFilterStore(updatedFilters);
  };
  const header = {
    [FilterModalType.Advanced]: {
      title: 'Your filters',
      subtitle: 'In the list below you can see the filters you have selected; you can remove one of them or clear all.',
    },
    [FilterModalType.Edit]: {
      title: 'Review selected filters',
      subtitle: 'The selected filters below will be used when creating a campaign. You can remove filters here. Please return to Streetview if you need to select more.',
    },
    [FilterModalType.Summary]: {
      title: 'Your filters',
      subtitle: 'In the list below you can see the filters you selected for a custom campaign.',
    },
  };

  const canEditFilters = type === FilterModalType.Advanced || type === FilterModalType.Edit;

  // Previously we were using an effect inappropriately here. This avoids the double-render
  if (
    previousState.type !== type
    || previousState.isOpen !== isOpen
    || previousState.selectedFilters !== selectedFilters
  ) {
    setPreviousState({ type, isOpen, selectedFilters });
    if (type === FilterModalType.Advanced && isOpen) {
      setFilterStoreCopy(structuredClone(selectedFilters));
    }
  }

  const applyFilterChanges = (newFilters: SelectedFilters) => {
    updateFilterStore(newFilters);
    onClose();
  };
  const removeFilterFromCopy = (category: string, key: string, filterDisplayName: string, value: FrontendFilterValue) => {
    const isRange = isRangeFilter(key, filterTypeLookup);
    setFilterStoreCopy((prev) => removeFilterValue(prev, category, key, filterDisplayName, value, isRange));
  };

  return type === FilterModalType.Advanced ? (
    <AdvancedModal
      headerIcon={customIcons.syncIcon}
      headerText={{
        title: header[type].title,
        subtitle: header[type].subtitle,
      }}
      actionButton={{
        label: 'Apply',
        class: 'button-default',
        function: () => applyFilterChanges(filterStoreCopy),
      }}
      onClose={onClose}
      isOpen={isOpen}
    >
      <FilterSummary
        filters={filterStoreCopy}
        onClick={canEditFilters ? removeFilterFromCopy : undefined}
      />
    </AdvancedModal>
  ) : (
    <Modal isOpen={isOpen} onClose={onClose} hasCloseBtn>
      <div id="modal-header" className="flex items-center text-start gap-2 w-full">
        <i className="m-4 text-2xl text-primary">{customIcons.syncIcon}</i>
        <InformationText
          title={header[type].title}
          subtitle={header[type].subtitle}
        />
      </div>
      <div id="modal-content" className="w-full overflow-y-scroll">
        <FilterSummary filters={filters || selectedFilters} onClick={canEditFilters ? removeFilter : undefined} />
      </div>
    </Modal>
  );
}
